import { useState, useEffect, useCallback, type ReactElement } from 'react';
import { useRepertoire } from '@/lib/queries/repertoire';
import { useInstruments, useAssessmentLevels } from '@/lib/queries/reference-data';
import { useCreatePracticeSession, useAddArrangementToSession } from '@/lib/queries/practice-queue';
import { cn } from '@/lib/cn';
import type { RepertoireEntry, AssessmentLevel } from '@/lib/types';

type Props = {
  onClose: () => void;
  /**
   * If set, pre-selects the matching repertoire entry once the search
   * results load. Useful when opening the dialog from a piece-detail
   * context where the arrangement is already known. No-op if no entry
   * matches.
   */
  defaultArrangementId?: string;
};

// Quick-log offers four canonical durations. 20m+ rounds up to 20 on
// the wire — the dialog is for fast logging; precise > 20-minute logs
// belong in the full Practice flow.
const DURATION_CHIPS: ReadonlyArray<{ minutes: number; label: string }> = [
  { minutes: 5, label: '5m' },
  { minutes: 10, label: '10m' },
  { minutes: 15, label: '15m' },
  { minutes: 20, label: '20m+' },
] as const;

const NOTES_MAX_LENGTH = 280;

const assessmentEmojis: Record<string, string> = {
  struggled: '😤',
  ok: '😐',
  good: '😊',
  nailed_it: '🔥',
};

type AssessmentButtonsProps = {
  levels: AssessmentLevel[];
  selectedId: string;
  onSelect: (id: string) => void;
};

function AssessmentButtons({ levels, selectedId, onSelect }: AssessmentButtonsProps): ReactElement {
  const sorted = [...levels].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {sorted.map((level) => {
        const emoji = assessmentEmojis[level.canonicalName] ?? '';
        const isSelected = selectedId === level.assessmentLevelId;
        return (
          <button
            key={level.assessmentLevelId}
            type="button"
            onClick={() => onSelect(level.assessmentLevelId)}
            aria-label={level.prettyName}
            aria-pressed={isSelected}
            className={cn(
              'flex flex-col items-center gap-1 min-h-[48px] px-2 py-2 border rounded-lg text-sm font-medium transition-colors',
              isSelected
                ? 'bg-[var(--brand)] text-white border-[var(--brand)]'
                : 'hover:bg-[var(--bg-soft)] active:bg-[var(--bg-inset)]',
            )}
          >
            <span className="text-xl" aria-hidden="true">
              {emoji}
            </span>
            <span>{level.prettyName}</span>
          </button>
        );
      })}
    </div>
  );
}

type ArrangementResultsProps = {
  entries: RepertoireEntry[];
  selectedId: string;
  onSelect: (entry: RepertoireEntry) => void;
};

function ArrangementResults({ entries, selectedId, onSelect }: ArrangementResultsProps): ReactElement {
  return (
    <ul
      className="border rounded-lg divide-y max-h-48 overflow-y-auto"
      role="listbox"
      aria-label="Repertoire search results"
    >
      {entries.map((entry) => {
        const isSelected = selectedId === entry.arrangementId;
        return (
          <li
            key={entry.userArrangementId}
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(entry)}
            className={cn(
              'px-3 py-2 cursor-pointer hover:bg-[var(--bg-soft)] text-sm',
              isSelected && 'bg-[var(--brand-wash)] font-medium',
            )}
          >
            <span className="font-medium">{entry.pieceName}</span>
            <span className="text-[var(--ink-3)] ml-1">— {entry.arrangementName}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function QuickLogDialog({ onClose, defaultArrangementId }: Props): ReactElement {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<RepertoireEntry | null>(null);
  const [selectedInstrumentId, setSelectedInstrumentId] = useState('');
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [selectedDurationMinutes, setSelectedDurationMinutes] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: repertoireData, isLoading: isSearching } = useRepertoire({
    search: debouncedSearch || undefined,
    size: 10,
  });
  const { data: instrumentsData } = useInstruments();
  const { data: assessmentData } = useAssessmentLevels();

  const createSession = useCreatePracticeSession();
  const addArrangement = useAddArrangementToSession();

  const isPending = createSession.isPending || addArrangement.isPending;

  const handleSearchChange = (value: string): void => {
    setSearchTerm(value);
    clearTimeout(
      (window as Window & { __quickLogSearchTimeout?: ReturnType<typeof setTimeout> })
        .__quickLogSearchTimeout,
    );
    (
      window as Window & { __quickLogSearchTimeout?: ReturnType<typeof setTimeout> }
    ).__quickLogSearchTimeout = setTimeout(() => setDebouncedSearch(value), 300);
  };

  const handleEscape = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // When defaultArrangementId is supplied (caller knows the
  // arrangement context, e.g. piece-detail), find the matching
  // repertoire entry and pre-select it once results load. Re-runs
  // when the prop or the repertoire result set changes.
  useEffect(() => {
    if (!defaultArrangementId) return;
    const entries = repertoireData?.content ?? [];
    if (entries.length === 0) return;
    if (selectedEntry?.arrangementId === defaultArrangementId) return;
    const match = entries.find((e) => e.arrangementId === defaultArrangementId);
    if (match) setSelectedEntry(match);
  }, [defaultArrangementId, repertoireData, selectedEntry]);

  const isSubmitDisabled =
    !selectedEntry || !selectedInstrumentId || !selectedAssessmentId || isPending;

  const handleSubmit = async (): Promise<void> => {
    if (!selectedEntry || !selectedInstrumentId || !selectedAssessmentId) return;
    setError(null);
    try {
      const session = await createSession.mutateAsync({
        instrumentId: selectedInstrumentId,
        practicedAt: new Date().toISOString(),
      });
      await addArrangement.mutateAsync({
        sessionId: session.practiceSessionId,
        arrangementId: selectedEntry.arrangementId,
        assessmentLevelId: selectedAssessmentId,
        notes: notes.trim() || undefined,
        ...(selectedDurationMinutes !== null
          ? { durationMinutes: selectedDurationMinutes }
          : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log practice');
    }
  };

  const repertoireEntries = repertoireData?.content ?? [];

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Quick log practice"
        className="relative bg-[var(--bg-surface)] rounded-t-[var(--r-lg)] sm:rounded-[var(--r-md)] w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-[var(--shadow-2)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          className="absolute top-4 right-4 text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          ✕
        </button>

        <h2 className="text-lg font-semibold mb-4">Log a tune</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="arrangement-search" className="block text-sm font-medium mb-1">
              Search arrangements
            </label>
            <input
              id="arrangement-search"
              type="text"
              placeholder="Search your repertoire..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-[var(--bg-surface)] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none focus:ring-2 focus:ring-[var(--ink)]"
            />
          </div>

          {isSearching && <p className="text-sm text-[var(--ink-3)]">Searching...</p>}

          {repertoireEntries.length > 0 && (
            <ArrangementResults
              entries={repertoireEntries}
              selectedId={selectedEntry?.arrangementId ?? ''}
              onSelect={setSelectedEntry}
            />
          )}

          {!isSearching && repertoireEntries.length === 0 && (
            <p className="text-sm text-[var(--ink-3)]">No arrangements found.</p>
          )}

          <div>
            <label htmlFor="instrument-select" className="block text-sm font-medium mb-1">
              Instrument
            </label>
            <select
              id="instrument-select"
              value={selectedInstrumentId}
              onChange={(e) => setSelectedInstrumentId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-[var(--bg-surface)] text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--ink)]"
            >
              <option value="">— Select instrument —</option>
              {instrumentsData?.content.map((instrument) => (
                <option key={instrument.instrumentId} value={instrument.instrumentId}>
                  {instrument.prettyName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="block text-sm font-medium mb-2">Duration (optional)</p>
            <div className="grid grid-cols-4 gap-2">
              {DURATION_CHIPS.map((chip) => {
                const isSelected = selectedDurationMinutes === chip.minutes;
                return (
                  <button
                    key={chip.minutes}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() =>
                      setSelectedDurationMinutes(isSelected ? null : chip.minutes)
                    }
                    className={cn(
                      'min-h-[36px] px-2 py-1.5 border rounded-lg text-sm font-medium transition-colors',
                      isSelected
                        ? 'bg-[var(--brand)] text-white border-[var(--brand)]'
                        : 'hover:bg-[var(--bg-soft)] active:bg-[var(--bg-inset)]',
                    )}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="block text-sm font-medium mb-2">How did it go?</p>
            {assessmentData && (
              <AssessmentButtons
                levels={assessmentData.content}
                selectedId={selectedAssessmentId}
                onSelect={setSelectedAssessmentId}
              />
            )}
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label htmlFor="quick-log-notes" className="block text-sm font-medium">
                Notes (optional)
              </label>
              <span
                className="text-xs text-[var(--ink-3)] tabular-nums"
                aria-live="polite"
              >
                {notes.length} / {NOTES_MAX_LENGTH}
              </span>
            </div>
            <textarea
              id="quick-log-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this run..."
              rows={2}
              maxLength={NOTES_MAX_LENGTH}
              className="w-full px-3 py-2 border rounded-lg bg-[var(--bg-surface)] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none focus:ring-2 focus:ring-[var(--ink)] resize-none"
            />
          </div>

          {error && (
            <div role="alert" className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitDisabled}
            className="w-full py-3 bg-[var(--brand)] text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--brand-ink)] transition-colors"
          >
            {isPending ? 'Logging...' : 'Log Practice'}
          </button>
        </div>
      </div>
    </div>
  );
}
